import { BaseMapper } from '@ps/utils';
import {
  IndividualQuota,
  QUALIFICATION_CODE,
  Quota,
  QuotaCriteria,
} from '@ps/survey-utils';

import {
  IndividualQuotaDto,
  SurveyQuotaEntryDto,
} from '../dtos/survey-quota.dto';

import { fromChildAgeGenderCriterion } from './domain/child-age-gender/child-age-gender-quota.mapper';
import { QuotaCriteriaMapper } from './quota.mapper';

export class SurveyQuotaMapper extends BaseMapper<SurveyQuotaEntryDto, Quota> {
  private readonly _criteriaMapper = new QuotaCriteriaMapper();

  public fromDto(dto: SurveyQuotaEntryDto): Quota {
    return {
      buyerQuotaId: dto.buyer_quota_id,
      quotaTitle: dto.quota_title ?? '',
      groupKey: dto.group_key,
      requiredCount: dto.required_count,
      flexible: dto.flexible,
      criteria: this._criteria(dto),
      percentage: dto.percentage,
      maximum: dto.maximum,
      minimum: dto.minimum,
      quotaCategory: dto.quota_category,
      currentTarget: dto.current_target,
      fielded: dto.achieved,
      individualQuotas: (dto.individual_quotas ?? []).map((individualQuota) =>
        this._individualQuotaFromDto(individualQuota),
      ),
    };
  }

  private _individualQuotaFromDto(dto: IndividualQuotaDto): IndividualQuota {
    return {
      allocation: {
        quantity: dto.allocation.quantity,
        percentage: dto.allocation.percentage,
        min: dto.allocation.min,
        max: dto.allocation.max,
      },
      criteria: this._criteriaMapper.fromDto(dto.criteria),
    };
  }

  // Normalizes the wire's own legacy 232 (child-age-gender) qualification code back to 218
  // ("Children") the moment it enters state — the UI only ever knows about 218 (see
  // visible-qualifications.helper.ts, which hides 232 from the catalog entirely), so state must
  // never hold 232 either. QuotaPayloadMapper does the reverse conversion when saving. Scoped to
  // this mapper specifically (the survey-edit fetch that feeds the create-survey quota drawer) —
  // QuotaCriteriaMapper itself stays generic, since QuotaMapper (Manage Survey's read-only
  // getQuotas) shares it too and has no use for the 218-shaped criteria.
  private _criteria(dto: SurveyQuotaEntryDto): QuotaCriteria[] {
    return this._criteriaMapper
      .fromDto(dto.criteria)
      .map((criterion, index) =>
        criterion.qualificationCode === QUALIFICATION_CODE.CHILD_AGE_GENDER
          ? fromChildAgeGenderCriterion(
              criterion,
              dto.criteria[index]?.child_age_unit,
            )
          : criterion,
      );
  }
}
